import { Row, Col, Card, Badge, Form, Stack, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { store } from "Store";
import { useDispatch } from "react-redux";
import { useEffect, useRef, useReducer } from "react";
import { FormTextInput } from "Components/Forms/FormInput";
import { generateWARpt, loadWAFormInfo, validateWARpt } from "./WebAnalyticReportAxiosFuncs";
import { formInfoReducer, ACTIONS } from "./WebAnalyticReportFormDataReducer";
import ErrorIcon from "@mui/icons-material/ErrorOutlined";
import { IconTextButton, TextButton, IconButton } from "Components/Button";
import { ERROR_MESSAGES } from "Utils/constants";
import { setGlobalErrorStatus } from "Components/StatusHandling";
import {
  applyDateSelectorFormat,
  getTodayDateInputValue,
  getLastMonthRange,
  getLastQuarterRange,
  getYearToDateRange
} from "Utils/utilityFunctions";
import { ICONS } from "Utils/icons";

/* mapping server field errors -> UI field names */
const nameConverter = {
  startDate: "startDate",
  endDate: "endDate",
  reportType: "reportTypeError",
  reportFileName: "fileName"
};

const emailSubscriptionListLabel = (
  <>
    Email Subscription List{" "}
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip id="emailSubscriptionListInfo" className="bodyS regular public-tooltip-light text-start">
          The selected date range does not apply to email subscriptions. All users currently
          subscribed to email notifications are included in this report.
        </Tooltip>
      }
    >
      <IconButton
        className="tooltip-btn-trigger"
        hiddenText="More information about Email Subscription List"
        variant="comp-plain"
        icon={ICONS.repoInfoTooltipTrigger}
      />
    </OverlayTrigger>
  </>
);

const WebAnalyticReport = () => {
  const loginState = store.getState().loginInfo;
  const token = "Bearer " + loginState.token;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const initialState = {
    data: "",
    loading: true,
    error: null
  };
  const [formInfo, formInfoDispatch] = useReducer(formInfoReducer, initialState);
  const parentCheckboxRef = useRef(null);

  const formFieldDisplay = {
    startDate: {
      type: "date",
      id: "startDate",
      name: "startDate",
      display: {
        label: "Start Date",
        description: "",
        title: ""
      },
      hideFieldOrgTypes: [],
      InputRef: useRef(null)
    },
    endDate: {
      type: "date",
      id: "endDate",
      name: "endDate",
      display: {
        label: "End Date",
        description: "",
        title: ""
      },
      hideFieldOrgTypes: [],
      InputRef: useRef(null)
    },
    fileName: {
      type: "text",
      id: "fileName",
      name: "fileName",
      display: {
        label: "Report File Name",
        description: (
          <>
            Report Name should not exceed 100 characters in length. <br/> Report
            Name should not have improper characters \ / : & * ? " &lt; &gt; | .
          </>
        ),
        title: ""
      },
      hideFieldOrgTypes: [],
      InputRef: useRef(null)
    },
    reportTypeChildren: {
      type: "checkbox",
      id: "reportTypeChildren",
      name: "reportTypeChildren",
      display: {
        label: "Website Analytics Report options",
        description: "",
        title: ""
      },
      hideFieldOrgTypes: [],
      InputRef: useRef(null)
    },
    reportTypeOther: {
      type: "checkbox",
      id: "reportTypeOther",
      name: "reportTypeOther",
      display: {
        label: "Additional report options",
        description: "",
        title: ""
      },
      hideFieldOrgTypes: [],
      InputRef: useRef(null)
    },
    reportTypeError: {
      type: "checkbox",
      id: "reportTypeError",
      name: "reportTypeError",
      display: {
        label: "Report Selection",
        description: "",
        title: ""
      },
      hideFieldOrgTypes: [],
      InputRef: useRef(null)
    }
  };

  const handleDateChange = (e) => {
    const val = applyDateSelectorFormat(e.target.value);
    formInfoDispatch({
      type: ACTIONS.DATE_CHANGE,
      value: val,
      name: e.target.name
    });
  };

  const handleTextChange = (e) => {
    formInfoDispatch({
      type: ACTIONS.TEXT_CHANGE,
      value: e.target.value,
      name: e.target.name
    });
  };

  const handleCheckboxChange = (e) => {
    formInfoDispatch({
      type: ACTIONS.CHECK_CHANGE,
      fieldName: e.target.name,
      id: e.target.id,
      checked: e.target.checked
    });
  };

  const handleParentToggle = () => {
    formInfoDispatch({
      type: ACTIONS.CHECK_TOGGLE_PARENT,
      fieldName: "reportTypeChildren"
    });
  };

  const handleSelectAll = () => {
    formInfoDispatch({ type: ACTIONS.CHECK_SELECT_ALL, fieldName: "reportTypeChildren" });
    formInfoDispatch({ type: ACTIONS.CHECK_SELECT_ALL, fieldName: "reportTypeOther" });
  };

  const handleClearAll = () => {
    formInfoDispatch({ type: ACTIONS.CHECK_CLEAR_ALL, fieldName: "reportTypeChildren" });
    formInfoDispatch({ type: ACTIONS.CHECK_CLEAR_ALL, fieldName: "reportTypeOther" });
  };

  const handleQuickRange = (getRange) => {
    const { start, end } = getRange();
    formInfoDispatch({ type: ACTIONS.DATE_CHANGE, value: start, name: "startDate" });
    formInfoDispatch({ type: ACTIONS.DATE_CHANGE, value: end, name: "endDate" });
  };

  const dispErrors = (fieldName) => {
    const data = formInfo.data[fieldName];
    const dispData = formFieldDisplay[fieldName];
    return (
      <div>
        {data.validation.errors.length > 0
          ? data.validation.errors.map((errorText, index) => (
            <Stack
              key={dispData.name + "Errors_" + index}
              direction="horizontal"
              gap={2}
              className="align-items-start"
            >
              <div id="errorIcon">
                <ErrorIcon fontSize="small"/>
              </div>
              <div>{errorText}</div>
            </Stack>
          ))
          : null}
      </div>
    );
  };

  /** Render the input label */
  const dispLabel = (fieldName, isHidden, getColLabelSizes, alignment, multi) => {
    const data = formInfo.data[fieldName];
    const dispData = formFieldDisplay[fieldName];

    if (alignment === Row) {
      if (multi === true) {
        return (
          <Col
            hidden={isHidden}
            xs={getColLabelSizes("xs")}
            sm={getColLabelSizes("sm")}
            md={getColLabelSizes("md")}
            lg={getColLabelSizes("lg")}
            xl={getColLabelSizes("xl")}
            xxl={getColLabelSizes("xxl")}
          >
            <legend>
              <span className="me-2">{dispData.display.label}</span>
              {data.validation.required ? "" : <Badge bg="optional">Optional</Badge>}
            </legend>
          </Col>
        );
      } else {
        return (
          <Form.Label
            visuallyHidden={isHidden}
            column
            xs={getColLabelSizes("xs")}
            sm={getColLabelSizes("sm")}
            md={getColLabelSizes("md")}
            lg={getColLabelSizes("lg")}
            xl={getColLabelSizes("xl")}
            xxl={getColLabelSizes("xxl")}
          >
            <span className="me-2">{dispData.display.label}</span>
            {data.validation.required ? "" : <Badge bg="optional">Optional</Badge>}
          </Form.Label>
        );
      }
    } else {
      if (multi === true) {
        return (
          <Row
            hidden={isHidden}
            xs={getColLabelSizes("xs")}
            sm={getColLabelSizes("sm")}
            md={getColLabelSizes("md")}
            lg={getColLabelSizes("lg")}
            xl={getColLabelSizes("xl")}
            xxl={getColLabelSizes("xxl")}
          >
            <legend>
              {dispData.display.label}
              {data.validation.required ? "" : <Badge bg="optional">Optional</Badge>}
            </legend>
          </Row>
        );
      } else {
        return (
          <Form.Label
            visuallyHidden={isHidden}
            xs={getColLabelSizes("xs")}
            sm={getColLabelSizes("sm")}
            md={getColLabelSizes("md")}
            lg={getColLabelSizes("lg")}
            xl={getColLabelSizes("xl")}
            xxl={getColLabelSizes("xxl")}
          >
            {dispData.display.label}
            {data.validation.required ? "" : <Badge bg="optional">Optional</Badge>}
          </Form.Label>
        );
      }
    }
  };

  /** get field value for submit */
  function getFieldValue(fieldName, data) {
    const values = data[fieldName];
    const dispValues = formFieldDisplay[fieldName];

    if (formInfo.loading) {
      return "";
    } else {
      const type = dispValues.type;
      switch (type) {
        case "text":
          return values.value ?? "";
        case "date":
          if (values.value != null) {
            const split = String(values.value).split("-");
            if (split.length === 3) {
              return split[1] + "/" + split[2] + "/" + split[0]; // mm/dd/yyyy
            }
            return values.value;
          }
          return "";
        case "checkbox":
          return (values?.checkBoxes ?? [])
            .filter((o) => o.isChecked)
            .map((o) => o.value);
        default:
          return "";
      }
    }
  }

  function addErrorMessages(fieldName, errorFieldName, data, isFirst) {
    const d = data.fieldErrors;
    const values = d?.[errorFieldName];

    if (values != null) {
      const field = formFieldDisplay[fieldName];
      const type = field.type;
      const err = values.errorMessages;

      if (isFirst === true) {
        field.InputRef.current?.focus();
      }

      switch (type) {
        case "text":
          formInfoDispatch({
            type: ACTIONS.TEXT_SET_ERRORS,
            name: fieldName,
            errors: err
          });
          break;
        case "date":
          formInfoDispatch({
            type: ACTIONS.TEXT_SET_ERRORS, // same pattern as your reference
            name: fieldName,
            errors: err
          });
          break;
        case "checkbox":
          formInfoDispatch({
            type: ACTIONS.CHECK_SET_ERRORS,
            fieldName: fieldName,
            errors: err
          });
          if (fieldName === "reportTypeError") {
            formInfoDispatch({ type: ACTIONS.CHECK_SET_ERRORS, fieldName: "reportTypeChildren", errors: [], isInvalid: true });
            formInfoDispatch({ type: ACTIONS.CHECK_SET_ERRORS, fieldName: "reportTypeOther", errors: [], isInvalid: true });
          }
          break;
        default:
          break;
      }
    }
  }

  function clearErrorMessages(data) {
    for (const key of Object.keys(data)) {
      const t = formFieldDisplay[key];
      switch (t.type) {
        case "text":
          formInfoDispatch({
            type: ACTIONS.TEXT_REMOVE_ERRORS,
            name: key
          });
          break;
        case "date":
          formInfoDispatch({
            type: ACTIONS.TEXT_REMOVE_ERRORS,
            name: key
          });
          break;
        case "checkbox":
          formInfoDispatch({
            type: ACTIONS.CHECK_REMOVE_ERRORS,
            fieldName: key
          });
          break;
        default:
          break;
      }
    }
  }

  const handleCancel = () => {
    navigate("/dashboard");
  };

  useEffect(() => {
    loadWAFormInfo(
      ACTIONS,
      formInfoDispatch,
      handleDateChange,
      handleTextChange,
      handleCheckboxChange,
      emailSubscriptionListLabel,
      token
    ).catch((error) => {
      const payload = error?.data ?? error;
      setGlobalErrorStatus({
        dispatch,
        popupData: {
          visible: true,
          title: ERROR_MESSAGES.DATA_ERROR.title,
          msg: payload?.data?.globalErrorMessageList?.[0] ?? ERROR_MESSAGES.DATA_ERROR.message,
          showContactHelp: true
        },
        redirect: {
          redirect: false,
          redirectFirst: false,
          path: "",
          data: {}
        },
        error: error
      });
    });
  }, []);

  const handleGenerate = async () => {
    const validation = await validateWARpt(
      token,
      formInfo,
      nameConverter,
      addErrorMessages,
      clearErrorMessages,
      getFieldValue
    ).catch((error) => {
      const payload = error?.data ?? error;
      setGlobalErrorStatus({
        dispatch: dispatch,
        popupData: {
          visible: true,
          title: ERROR_MESSAGES.DATA_ERROR.title,
          msg: payload?.data?.globalErrorMessageList?.[0] ?? ERROR_MESSAGES.DATA_ERROR.message,
          showContactHelp: true
        },
        redirect: {
          redirect: false,
          redirectFirst: false,
          path: "",
          data: {}
        },
        error: error
      });
    });

    if (validation?.data?.status === 1) {
      const res = await generateWARpt(
        formInfo,
        "Bearer " + loginState.token,
        getFieldValue
      ).catch((error) => {
        const payload = error?.data ?? error;
        setGlobalErrorStatus({
          dispatch: dispatch,
          popupData: {
            visible: true,
            title: ERROR_MESSAGES.DATA_ERROR.title,
            msg: payload?.data?.globalErrorMessageList?.[0] ?? ERROR_MESSAGES.DATA_ERROR.message,
            showContactHelp: true
          },
          redirect: {
            redirect: false,
            redirectFirst: false,
            path: "",
            data: {}
          },
          error: error
        });
      });

      if (res?.status?.code === 1) {
        const fileName =
          res.data.headers["content-disposition"]
            ?.split("filename=")[1]
            .split(";")[0]
            .replace(/^"(.*)"$/, "$1") || "web-analytics-report.csv";

        const url = window.URL.createObjectURL(res.data.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        setGlobalErrorStatus({
          dispatch: dispatch,
          popupData: {
            visible: true,
            title: ERROR_MESSAGES.DATA_ERROR.title,
            msg: ERROR_MESSAGES.DATA_ERROR.message,
            showContactHelp: true
          },
          redirect: {
            redirect: false,
            redirectFirst: false,
            path: "",
            data: {}
          },
          error: res?.data?.status?.text
        });
      }
    }
  };

  const childBoxes = formInfo.loading ? [] : formInfo.data.reportTypeChildren.checkBoxes;
  const otherBoxes = formInfo.loading ? [] : formInfo.data.reportTypeOther.checkBoxes;
  const allChildrenChecked = childBoxes.length > 0 && childBoxes.every((c) => c.isChecked);
  const someChildrenChecked = childBoxes.some((c) => c.isChecked);

  const combinedRealBoxes = [...childBoxes, ...otherBoxes];
  const allReportsSelected = combinedRealBoxes.length > 0 && combinedRealBoxes.every((c) => c.isChecked);
  const noReportsSelected = combinedRealBoxes.every((c) => !c.isChecked);
  const todayDateValue = getTodayDateInputValue();

  useEffect(() => {
    if (parentCheckboxRef.current) {
      parentCheckboxRef.current.indeterminate = someChildrenChecked && !allChildrenChecked;
    }
  }, [someChildrenChecked, allChildrenChecked]);

  return (
    <Row>
      {formInfo.loading ? null : (
        <Col xl={11} lg={11} md={11} sm={12}>
          <Card className="p-4">
            <Card.Body className="text-start">
              <h1 id="mainContent" className="header1 text-start mb-3 blue bold">
                Website Analytics Report
              </h1>
              <p>
                The Website Analytics Report displays information on the times various components of the website have
                been accessed, including user login, report generation and document download count.
              </p>
              <p>
                Answer the questions below to create your report.
              </p>
              <br/>
              <Form>
                <h2>Reporting Period</h2>
                <Row>
                  <Col lg={4} xl={4} md={5} sm={5} xs={8} className="d-flex flex-column align-items-stretch c-g-xs-8">
                    <p className="mb-0">Report Start Date</p>
                    <FormTextInput
                      orgType={"Admin"}
                      fieldName="startDate"
                      hideLabel={true}
                      max={todayDateValue}
                      horizontalForm={{
                        is: false,
                        labelCols: {},
                        inputCols: { lg: 12 }
                      }}                                
                      displayLabelFunc={dispLabel}
                      data={formInfo.data}
                      fieldData={formFieldDisplay}
                      displayErrorsFunc={dispErrors}
                    />
                  </Col>
                  <Col lg={4} xl={4} md={5} sm={5} xs={8} className="d-flex flex-column align-items-stretch c-g-xs-8">
                    <p className="mb-0">Report End Date</p>
                    <FormTextInput
                      orgType={"Admin"}
                      fieldName="endDate"
                      hideLabel={true}
                      horizontalForm={{
                        is: false,
                        labelCols: {},
                        inputCols: { lg: 12 }
                      }}
                      displayLabelFunc={dispLabel}
                      data={formInfo.data}
                      fieldData={formFieldDisplay}
                      displayErrorsFunc={dispErrors}
                    />
                  </Col>
                </Row>

                <p className="body regular mb-2 mt-2">Quick Ranges</p>
                <Stack direction="horizontal" gap={3} className="flex-wrap mb-3">
                  <TextButton
                    variant="comp-clear"
                    size="lg"
                    content="Last Month"
                    onClick={() => handleQuickRange(getLastMonthRange)}
                  />
                  <TextButton
                    variant="comp-clear"
                    size="lg"
                    content="Last Quarter"
                    onClick={() => handleQuickRange(getLastQuarterRange)}
                  />
                  <TextButton
                    variant="comp-clear"
                    size="lg"
                    content="Year to Date"
                    onClick={() => handleQuickRange(getYearToDateRange)}
                  />
                </Stack>

                <Row className="align-items-center report-type-header-row">
                  <Col>
                    <h2 id="reportTypeHeading" className="m-0 pb-2 pb-sm-0 border-0 mt-3">Report Selection</h2>
                  </Col>
                  <Col xs="auto">
                    <Stack direction="horizontal" gap={3}>
                      <TextButton
                        variant="comp-clear"
                        size="lg"
                        content="Select All"
                        onClick={handleSelectAll}
                        disabled={allReportsSelected}
                      />
                      <TextButton
                        variant="comp-clear"
                        size="lg"
                        content="Clear"
                        onClick={handleClearAll}
                        disabled={noReportsSelected}
                      />
                    </Stack>
                  </Col>
                </Row>
                <div role="group" aria-labelledby="reportTypeHeading">
                  <Form.Check
                    ref={parentCheckboxRef}
                    type="checkbox"
                    id="reportTypeParent"
                    label="Website Analytics Report"
                    checked={allChildrenChecked}
                    isInvalid={formInfo.data.reportTypeChildren.validation.isInvalid ? "invalid" : ""}
                    onChange={handleParentToggle}
                  />
                  <p className="ps-4 body regular report-type-description">
                    This report includes Document Download Log, Interactive Report Access Log, PSOPPC Login Count, and Solr Search Log.
                  </p>
                  <div className="ps-4">
                    <FormTextInput
                      orgType={"Admin"}
                      fieldName="reportTypeChildren"
                      hideLabel={true}
                      horizontalForm={{
                        is: false,
                        labelCols: {},
                        inputCols: { lg: 12 }
                      }}
                      displayLabelFunc={dispLabel}
                      data={formInfo.data}
                      fieldData={formFieldDisplay}
                      displayErrorsFunc={dispErrors}
                    />
                  </div>
                  <FormTextInput
                    orgType={"Admin"}
                    fieldName="reportTypeOther"
                    hideLabel={true}
                    horizontalForm={{
                      is: false,
                      labelCols: {},
                      inputCols: { lg: 12 }
                    }}
                    displayLabelFunc={dispLabel}
                    data={formInfo.data}
                    fieldData={formFieldDisplay}
                    displayErrorsFunc={dispErrors}
                  />
                  <Form.Control.Feedback
                    className={formInfo.data.reportTypeError.validation.isInvalid ? "is-invalid" : ""}
                    type="invalid"
                  >
                    {dispErrors("reportTypeError")}
                  </Form.Control.Feedback>
                </div>

                <h2>File Name</h2>
                <FormTextInput
                  orgType={"Admin"}
                  fieldName="fileName"
                  hideLabel={false}
                  horizontalForm={{
                    is: true,
                    labelCols: { lg: 3, md: 4 },
                    inputCols: { lg: 6, xl: 5 }
                  }}
                  displayLabelFunc={dispLabel}
                  data={formInfo.data}
                  fieldData={formFieldDisplay}
                  displayErrorsFunc={dispErrors}
                />
                <br />
                <Col className="d-flex gap-4">
                  <IconTextButton
                    className="reverse"
                    icon={ICONS.download}
                    onClick={handleGenerate}
                    variant="normal"
                    content={"Generate Report"}
                  />
                  <TextButton
                    variant="outline"
                    onClick={handleCancel}
                    content={"Cancel"}
                  />
                </Col>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      )}
    </Row>
  );
};

export default WebAnalyticReport;
